import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { VerifiedTick, type VerifiedTickSize } from '../VerifiedTick/VerifiedTick';
import type { VerifiableEntityType } from '../VerifiedTick/entityTypes';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  readonly src?: string | undefined;
  readonly alt?: string | undefined;
  readonly name?: string | undefined;
  readonly size?: AvatarSize;
  /** Optional status pip — small dot on the bottom-end corner. */
  readonly status?: 'online' | 'busy' | 'offline' | undefined;
  /**
   * F09 v2 — overlay the verified blue tick on the bottom-end corner (on a
   * surface-coloured halo). Always inert (`interactive={false}`): an avatar is
   * usually inside a clickable row/card. When set, a `status` pip moves to the
   * top-end corner so the two never overlap.
   */
  readonly verified?: boolean | undefined;
  /** Entity kind for the tick's accessible label ("Verified trainer", …). */
  readonly verifiedEntityType?: VerifiableEntityType | undefined;
}

const SIZE: Record<AvatarSize, { box: string; text: string; pip: string }> = {
  xs: { box: 'size-6 text-[10px]', text: 'text-[10px]', pip: 'size-1.5' },
  sm: { box: 'size-8 text-[11px]', text: 'text-[11px]', pip: 'size-2' },
  md: { box: 'size-10 text-[13px]', text: 'text-[13px]', pip: 'size-2.5' },
  lg: { box: 'size-14 text-[16px]', text: 'text-[16px]', pip: 'size-3' },
  xl: { box: 'size-20 text-[22px]', text: 'text-[22px]', pip: 'size-3.5' },
};

const TICK_SIZE: Record<AvatarSize, VerifiedTickSize> = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'sm',
  xl: 'md',
};

const STATUS_COLOR: Record<NonNullable<AvatarProps['status']>, string> = {
  online: 'bg-success',
  busy: 'bg-warning',
  offline: 'bg-muted',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
}

export function Avatar({
  src,
  alt,
  name,
  size = 'md',
  status,
  verified = false,
  verifiedEntityType,
  className,
  ...rest
}: AvatarProps) {
  const sz = SIZE[size];
  return (
    <span className={cn('relative inline-flex shrink-0', className)} {...rest}>
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full overflow-hidden font-bold uppercase',
          'bg-surface-2 text-ink-2',
          sz.box,
        )}
      >
        {src ? (
          <img
            src={src}
            alt={alt ?? name ?? ''}
            className="size-full object-cover"
            loading="lazy"
          />
        ) : name ? (
          <span>{initials(name)}</span>
        ) : null}
      </span>
      {status ? (
        <span
          aria-hidden="true"
          className={cn(
            'absolute end-0 rounded-full ring-2 ring-surface',
            verified ? 'top-0' : 'bottom-0',
            STATUS_COLOR[status],
            sz.pip,
          )}
        />
      ) : null}
      {verified ? (
        <span
          data-testid="avatar-verified-tick"
          className="absolute bottom-0 end-0 inline-flex rounded-full bg-surface leading-none ring-2 ring-surface"
        >
          <VerifiedTick
            size={TICK_SIZE[size]}
            entityType={verifiedEntityType}
            interactive={false}
          />
        </span>
      ) : null}
    </span>
  );
}
